import { expansionsInSelectFormat, FILTER_CONFIG, initialState, qualityTiers } from "@/constants";
import { allSelectableCategoryValues } from "@/constants/domain/hierarchicalCategory";
import { getApiBaseUrl } from "@/helpers/helpers";
import { getApiService } from "@/services/apiService";
import { AuthStore, AuthTokens, ChangelogEntry, ChangelogResponse, Option, UserData } from "@/types";
import { EnhancedAuctionItem, FacetCounts } from "@/types/domain/auction";
import { create } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { Sentry, reportAppError, reportApiFailure } from '@/helpers/sentry';

export interface DataState {
  fullAHData: EnhancedAuctionItem[];
  setFullAHData: (value: EnhancedAuctionItem[]) => void;

  filteredAHData: EnhancedAuctionItem[];
  setFilteredAHData: (value: EnhancedAuctionItem[]) => void;

  fullDropshipData: EnhancedAuctionItem[];
  setFullDropshipData: (value: EnhancedAuctionItem[]) => void;

  filteredDropshipData: EnhancedAuctionItem[];
  setFilteredDropshipData: (value: EnhancedAuctionItem[]) => void;

  fullHousingData: EnhancedAuctionItem[];
  setFullHousingData: (value: EnhancedAuctionItem[]) => void;

  filteredHousingData: EnhancedAuctionItem[];
  setFilteredHousingData: (value: EnhancedAuctionItem[]) => void;

  fullInvAHData: EnhancedAuctionItem[];
  setFullInvAHData: (value: EnhancedAuctionItem[]) => void;

  /**
   * Drill-down facet counts from the most recent fetch. Null when the
   * backend didn't emit them (e.g. dropship / housing combined flows).
   * Stored alongside the data so the sidebar can read counts without
   * re-running queries.
   */
  ahFacets: FacetCounts | null;
  setAHFacets: (value: FacetCounts | null) => void;

  lastModifiedUS: number;
  setLastModifiedUS: (value: number) => void;

  lastModifiedEU: number;
  setLastModifiedEU: (value: number) => void;
}

export const useData = create<DataState>()(
  persist(
    devtools(
      (set) => ({
        fullAHData: [],
        setFullAHData: (value: EnhancedAuctionItem[]) => set(() => ({ fullAHData: value })),

        filteredAHData: [],
        setFilteredAHData: (value: EnhancedAuctionItem[]) => set(() => ({ filteredAHData: value })),

        fullDropshipData: [],
        setFullDropshipData: (value: EnhancedAuctionItem[]) => set(() => ({ fullDropshipData: value })),

        filteredDropshipData: [],
        setFilteredDropshipData: (value: EnhancedAuctionItem[]) => set(() => ({ filteredDropshipData: value })),

        fullHousingData: [],
        setFullHousingData: (value: EnhancedAuctionItem[]) => set(() => ({ fullHousingData: value })),

        filteredHousingData: [],
        setFilteredHousingData: (value: EnhancedAuctionItem[]) => set(() => ({ filteredHousingData: value })),

        fullInvAHData: [],
        setFullInvAHData: (value: EnhancedAuctionItem[]) => set(() => ({ fullInvAHData: value })),

        ahFacets: null,
        setAHFacets: (value: FacetCounts | null) => set(() => ({ ahFacets: value })),

        lastModifiedUS: 0,
        setLastModifiedUS: (value: number) => set(() => ({ lastModifiedUS: value })),

        lastModifiedEU: 0,
        setLastModifiedEU: (value: number) => set(() => ({ lastModifiedEU: value })),
      }),
      {
        name: "useData",
      }
    ),
    {
      name: 'data-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        lastModifiedUS: state.lastModifiedUS,
        lastModifiedEU: state.lastModifiedEU,
      }),
    }
  )
);

export interface FilterState {
  hasBeenInitializedFromConfig: boolean;
  setHasBeenInitializedFromConfig: (value: boolean) => void;

  selectedCustomCategories: string[];
  setSelectedCustomCategories: (value: string[]) => void;

  selectedExpansions: Option[];
  setSelectedExpansions: (value: Option[]) => void;

  selectedQualities: Option[];
  setSelectedQualities: (value: Option[]) => void;

  sellRateFilterVal: [number, number];
  setSellRateFilterVal: (value: [number, number]) => void;

  saleAvgFilterVal: [number, number];
  setSaleAvgFilterVal: (value: [number, number]) => void;

  saleAvgVsBuyPercentFilterVal: [number, number];
  setSaleAvgVsBuyPercentFilterVal: (value: [number, number]) => void;

  goldProfitFilterVal: [number, number];
  setGoldProfitFilterVal: (value: [number, number]) => void;

  goldProfitSaleAvgFilterVal: [number, number];
  setGoldProfitSaleAvgFilterVal: (value: [number, number]) => void;

  goldProfitPercentFilterVal: [number, number];
  setGoldProfitPercentFilterVal: (value: [number, number]) => void;

  maxGoldBuyoutFilterVal: [number, number];
  setMaxGoldBuyoutFilterVal: (value: [number, number]) => void;

  ilvlFilterVal: [number, number];
  setIlvlFilterVal: (value: [number, number]) => void;

  nameFilterVal: string;
  setNameFilterVal: (value: string) => void;

  buyingRealmsFilterVal: Option[];
  setBuyingRealmsFilterVal: (value: Option[]) => void;

  sellingRealmsFilterVal: Option[];
  setSellingRealmsFilterVal: (value: Option[]) => void;

  uncheckedItemIds: string[];
  setUncheckedItemIds: (value: string[] | ((prev: string[]) => string[])) => void;

  numberOfResultsPerPage: number;
  currentPage: number;
  setCurrentPage: (value: number) => void;

  showPositiveSaleAvgVsBuy: boolean;
  setShowPositiveSaleAvgVsBuy: (value: boolean) => void;

  filterCraftedItems: string;
  setFilterCraftedItems: (value: string) => void;

  // 'filter' (default): filter changes only narrow the already-loaded dataset
  // client-side. 'recalculate': filter changes trigger a backend recalculation.
  filterMode: FilterMode;
  setFilterMode: (value: FilterMode) => void;
  // Captures a baseline if 'filter' mode is active without one (e.g. it is
  // the default/persisted state, so setFilterMode never ran). Called by the
  // tool pages' fetches so the values that produced the loaded dataset always
  // become the baseline.
  ensureFilterModeBaseline: () => void;

  // Snapshot of the filter values captured when entering 'filter' mode.
  // Server fetches (SSE auto-refresh, mount, explicit apply) use this baseline
  // while filter mode is active, so the loaded dataset stays as wide as it was
  // when the user started narrowing client-side — otherwise a background
  // refresh would silently shrink it to the narrowed values and widening a
  // filter could no longer bring items back. Managed by setFilterMode;
  // re-captured by refreshFilterModeBaseline after bulk state changes
  // (preset apply, reset) that redefine what the user considers "the baseline".
  filterModeBaseline: ServerFilterSnapshot | null;
  refreshFilterModeBaseline: () => void;

  isFilterPanelOpen: boolean;
  setIsFilterPanelOpen: (value: boolean) => void;
}

export type FilterMode = 'recalculate' | 'filter';

// The filter fields that are sent to the backend when computing results.
// Realm selections are included: in 'filter' mode a realm change never
// triggers a recalculation — loaded rows are narrowed client-side by their
// computed best buy/sell realm instead. Note that narrowing realms this way
// can hide flips a recalculation would still find via a different realm
// (each row only carries the single best realm pair); that trade-off is
// deliberate — filter mode never talks to the server.
export interface ServerFilterSnapshot {
  selectedCustomCategories: string[];
  selectedQualities: Option[];
  selectedExpansions: Option[];
  sellRateFilterVal: [number, number];
  saleAvgFilterVal: [number, number];
  saleAvgVsBuyPercentFilterVal: [number, number];
  goldProfitFilterVal: [number, number];
  goldProfitSaleAvgFilterVal: [number, number];
  goldProfitPercentFilterVal: [number, number];
  maxGoldBuyoutFilterVal: [number, number];
  ilvlFilterVal: [number, number];
  buyingRealmsFilterVal: Option[];
  sellingRealmsFilterVal: Option[];
  filterCraftedItems: string;
  showPositiveSaleAvgVsBuy: boolean;
}

const captureServerFilterSnapshot = (state: FilterState): ServerFilterSnapshot => ({
  selectedCustomCategories: state.selectedCustomCategories,
  selectedQualities: state.selectedQualities,
  selectedExpansions: state.selectedExpansions,
  sellRateFilterVal: state.sellRateFilterVal,
  saleAvgFilterVal: state.saleAvgFilterVal,
  saleAvgVsBuyPercentFilterVal: state.saleAvgVsBuyPercentFilterVal,
  goldProfitFilterVal: state.goldProfitFilterVal,
  goldProfitSaleAvgFilterVal: state.goldProfitSaleAvgFilterVal,
  goldProfitPercentFilterVal: state.goldProfitPercentFilterVal,
  maxGoldBuyoutFilterVal: state.maxGoldBuyoutFilterVal,
  ilvlFilterVal: state.ilvlFilterVal,
  buyingRealmsFilterVal: state.buyingRealmsFilterVal,
  sellingRealmsFilterVal: state.sellingRealmsFilterVal,
  filterCraftedItems: state.filterCraftedItems,
  showPositiveSaleAvgVsBuy: state.showPositiveSaleAvgVsBuy,
});

// The filter values a server fetch should use: the baseline captured on
// entering 'filter' mode while it is active, the live state otherwise.
export const serverFilterStateOf = (state: FilterState): ServerFilterSnapshot => {
  const live = captureServerFilterSnapshot(state);
  if (state.filterMode !== 'filter' || !state.filterModeBaseline) return live;
  // The baseline is persisted to localStorage, so it can predate fields added
  // in newer builds (its shape has already changed once during development).
  // Merge field-wise over the live snapshot so a missing field never reaches
  // a fetch as undefined and crashes the page into its error screen.
  const merged = { ...live };
  const baseline = state.filterModeBaseline as Partial<Record<keyof ServerFilterSnapshot, unknown>>;
  for (const key of Object.keys(live) as (keyof ServerFilterSnapshot)[]) {
    const value = baseline[key];
    if (value !== undefined) {
      (merged as Record<keyof ServerFilterSnapshot, unknown>)[key] = value;
    }
  }
  return merged;
};

export const useFilter = create<FilterState>()(
  persist(
    devtools(
      (set) => ({
        hasBeenInitializedFromConfig: false,
        setHasBeenInitializedFromConfig: (value: boolean) =>
          set(() => ({ hasBeenInitializedFromConfig: value })),

        selectedCustomCategories: allSelectableCategoryValues,
        setSelectedCustomCategories: (value: string[]) =>
          set(() => ({ selectedCustomCategories: value })),

      selectedExpansions: expansionsInSelectFormat,
      setSelectedExpansions: (value: Option[]) =>
        set(() => ({ selectedExpansions: value })),

      selectedQualities: qualityTiers,
      setSelectedQualities: (value: Option[]) =>
        set(() => ({ selectedQualities: value })),

      sellRateFilterVal: [FILTER_CONFIG.SELL_RATE.MIN, FILTER_CONFIG.SELL_RATE.MAX],
      setSellRateFilterVal: (value: [number, number]) =>
        set(() => ({ sellRateFilterVal: value })),

      saleAvgFilterVal: [FILTER_CONFIG.SALE_AVG.MIN, FILTER_CONFIG.SALE_AVG.MAX],
      setSaleAvgFilterVal: (value: [number, number]) =>
        set(() => ({ saleAvgFilterVal: value })),

      saleAvgVsBuyPercentFilterVal: [FILTER_CONFIG.SALE_AVG_VS_BUY_PERCENT.MIN, FILTER_CONFIG.SALE_AVG_VS_BUY_PERCENT.MAX],
      setSaleAvgVsBuyPercentFilterVal: (value: [number, number]) =>
        set(() => ({ saleAvgVsBuyPercentFilterVal: value })),

      goldProfitFilterVal: [FILTER_CONFIG.GOLD_PROFIT.MIN, FILTER_CONFIG.GOLD_PROFIT.MAX],
      setGoldProfitFilterVal: (value: [number, number]) =>
        set(() => ({ goldProfitFilterVal: value })),

      goldProfitSaleAvgFilterVal: [FILTER_CONFIG.GOLD_PROFIT_SALE_AVG.MIN, FILTER_CONFIG.GOLD_PROFIT_SALE_AVG.MAX],
      setGoldProfitSaleAvgFilterVal: (value: [number, number]) =>
        set(() => ({ goldProfitSaleAvgFilterVal: value })),

      goldProfitPercentFilterVal: [FILTER_CONFIG.GOLD_PROFIT_PERCENT.MIN, FILTER_CONFIG.GOLD_PROFIT_PERCENT.MAX],
      setGoldProfitPercentFilterVal: (value: [number, number]) =>
        set(() => ({ goldProfitPercentFilterVal: value })),

      maxGoldBuyoutFilterVal: [FILTER_CONFIG.GOLD_BUYOUT.MIN, FILTER_CONFIG.GOLD_BUYOUT.MAX],
      setMaxGoldBuyoutFilterVal: (value: [number, number]) =>
        set(() => ({ maxGoldBuyoutFilterVal: value })),

      ilvlFilterVal: [FILTER_CONFIG.ILVL.MIN, FILTER_CONFIG.ILVL.MAX],
      setIlvlFilterVal: (value: [number, number]) =>
        set(() => ({ ilvlFilterVal: value })),

      nameFilterVal: "",
      setNameFilterVal: (value: string) =>
        set(() => ({ nameFilterVal: value })),

      buyingRealmsFilterVal: [],
      setBuyingRealmsFilterVal: (value: Option[]) =>
        set(() => ({ buyingRealmsFilterVal: value })),

      sellingRealmsFilterVal: [],
      setSellingRealmsFilterVal: (value: Option[]) =>
        set(() => ({ sellingRealmsFilterVal: value })),

      uncheckedItemIds: [],
      setUncheckedItemIds: (value) => set((state) => ({
        uncheckedItemIds: typeof value === 'function' ? value(state.uncheckedItemIds) : value
      })),

      numberOfResultsPerPage: 100,
      currentPage: 1,
      setCurrentPage: (value: number) => set(() => ({ currentPage: value })),

      showPositiveSaleAvgVsBuy: false,
      setShowPositiveSaleAvgVsBuy: (value: boolean) =>
        set(() => ({ showPositiveSaleAvgVsBuy: value })),

      filterCraftedItems: "include",
      setFilterCraftedItems: (value: string) =>
        set(() => ({ filterCraftedItems: value })),

      filterMode: "filter",
      setFilterMode: (value: FilterMode) =>
        set((state) => {
          if (value === state.filterMode) return {};
          return {
            filterMode: value,
            filterModeBaseline:
              value === "filter" ? captureServerFilterSnapshot(state as FilterState) : null,
          };
        }),
      filterModeBaseline: null,
      ensureFilterModeBaseline: () =>
        set((state) => (
          state.filterMode === "filter" && !state.filterModeBaseline
            ? { filterModeBaseline: captureServerFilterSnapshot(state as FilterState) }
            : {}
        )),
      refreshFilterModeBaseline: () =>
        set((state) => (
          state.filterMode === "filter"
            ? { filterModeBaseline: captureServerFilterSnapshot(state as FilterState) }
            : {}
        )),

      isFilterPanelOpen: false,
      setIsFilterPanelOpen: (value: boolean) =>
        set(() => ({ isFilterPanelOpen: value })),
    }),
    {
      name: "useFilter",
    }
  ),
  {
    name: 'filter-storage',
    version: 1,
    storage: createJSONStorage(() => localStorage),
    partialize: (state) => ({
      hasBeenInitializedFromConfig: state.hasBeenInitializedFromConfig,
      selectedCustomCategories: state.selectedCustomCategories,
      selectedExpansions: state.selectedExpansions,
      selectedQualities: state.selectedQualities,
      sellRateFilterVal: state.sellRateFilterVal,
      saleAvgFilterVal: state.saleAvgFilterVal,
      saleAvgVsBuyPercentFilterVal: state.saleAvgVsBuyPercentFilterVal,
      goldProfitFilterVal: state.goldProfitFilterVal,
      goldProfitSaleAvgFilterVal: state.goldProfitSaleAvgFilterVal,
      goldProfitPercentFilterVal: state.goldProfitPercentFilterVal,
      maxGoldBuyoutFilterVal: state.maxGoldBuyoutFilterVal,
      ilvlFilterVal: state.ilvlFilterVal,
      buyingRealmsFilterVal: state.buyingRealmsFilterVal,
      sellingRealmsFilterVal: state.sellingRealmsFilterVal,
      uncheckedItemIds: state.uncheckedItemIds,
      showPositiveSaleAvgVsBuy: state.showPositiveSaleAvgVsBuy,
      filterCraftedItems: state.filterCraftedItems,
      filterMode: state.filterMode,
      filterModeBaseline: state.filterModeBaseline,
    }),
  }
  )
);

// Retail TSM List Generator Store Types
export interface RetailTSMListGeneratorState {
  fullAHData: EnhancedAuctionItem[];
  setFullAHData: (value: EnhancedAuctionItem[]) => void;

  filteredAHData: EnhancedAuctionItem[];
  setFilteredAHData: (value: EnhancedAuctionItem[]) => void;

  selectedCustomCategories: string[];
  setSelectedCustomCategories: (value: string[]) => void;

  selectedExpansions: Option[];
  setSelectedExpansions: (value: Option[]) => void;

  sellRateFilterVal: [number, number];
  setSellRateFilterVal: (value: [number, number]) => void;

  saleAvgFilterVal: [number, number];
  setSaleAvgFilterVal: (value: [number, number]) => void;

  regionMarketValueFilterVal: [number, number];
  setRegionMarketValueFilterVal: (value: [number, number]) => void;

  regionHistoricalFilterVal: [number, number];
  setRegionHistoricalFilterVal: (value: [number, number]) => void;

  nameFilterVal: string;
  setNameFilterVal: (value: string) => void;

  uncheckedItemIds: string[];
  setUncheckedItemIds: (value: string[] | ((prev: string[]) => string[])) => void;

  filterCraftedItems: string;
  setFilterCraftedItems: (value: string) => void;

  numberOfResultsPerPage: number;
  currentPage: number;
  setCurrentPage: (value: number) => void;
}

export const useRetailTSMListGenerator = create<RetailTSMListGeneratorState>()(
  devtools(
    (set) => ({
      fullAHData: [],
      setFullAHData: (value: EnhancedAuctionItem[]) => set(() => ({ fullAHData: value })),

      filteredAHData: [],
      setFilteredAHData: (value: EnhancedAuctionItem[]) => set(() => ({ filteredAHData: value })),

      selectedCustomCategories: allSelectableCategoryValues,
      setSelectedCustomCategories: (value: string[]) =>
        set(() => ({ selectedCustomCategories: value })),

      selectedExpansions: expansionsInSelectFormat,
      setSelectedExpansions: (value: Option[]) =>
        set(() => ({ selectedExpansions: value })),

      sellRateFilterVal: [FILTER_CONFIG.SELL_RATE.MIN, FILTER_CONFIG.SELL_RATE.MAX],
      setSellRateFilterVal: (value: [number, number]) =>
        set(() => ({ sellRateFilterVal: value })),

      saleAvgFilterVal: [FILTER_CONFIG.SALE_AVG.MIN, FILTER_CONFIG.SALE_AVG.MAX],
      setSaleAvgFilterVal: (value: [number, number]) =>
        set(() => ({ saleAvgFilterVal: value })),

      regionMarketValueFilterVal: [FILTER_CONFIG.REGION_MARKET_VALUE.MIN, FILTER_CONFIG.REGION_MARKET_VALUE.MAX],
      setRegionMarketValueFilterVal: (value: [number, number]) =>
        set(() => ({ regionMarketValueFilterVal: value })),

      regionHistoricalFilterVal: [FILTER_CONFIG.REGION_HISTORICAL.MIN, FILTER_CONFIG.REGION_HISTORICAL.MAX],
      setRegionHistoricalFilterVal: (value: [number, number]) =>
        set(() => ({ regionHistoricalFilterVal: value })),

      nameFilterVal: "",
      setNameFilterVal: (value: string) =>
        set(() => ({ nameFilterVal: value })),

      uncheckedItemIds: [],
      setUncheckedItemIds: (value) => set((state) => ({
        uncheckedItemIds: typeof value === 'function' ? value(state.uncheckedItemIds) : value
      })),

      filterCraftedItems: "include",
      setFilterCraftedItems: (value: string) =>
        set(() => ({ filterCraftedItems: value })),

      numberOfResultsPerPage: 100,
      currentPage: 1,
      setCurrentPage: (value: number) => set(() => ({ currentPage: value })),
    }),
    {
      name: "useRetailTSMListGenerator",
    }
  )
);

export const useAuth = create<AuthStore>()(
  persist(
    (set, get) => ({
      ...initialState,

      setIsAuthenticated: (isAuthenticated: boolean) =>
        set({ isAuthenticated }),

      setIsAdmin: (isAdmin: boolean) =>
        set({ isAdmin }),

      setUserData: (userData: UserData | null) =>
        set({ userData }),

      setTokens: (tokens: AuthTokens | null) =>
        set({ tokens }),

      setIsLoading: (isLoading: boolean) =>
        set({ isLoading }),

      clearAuth: () =>
        set({
          isAuthenticated: false,
          userData: null,
          tokens: null,
          isLoading: false,
        }),

      updateAccessToken: (accessToken: string) => {
        const currentTokens = get().tokens;
        if (currentTokens) {
          set({
            tokens: {
              ...currentTokens,
              accessToken,
            },
          });
        }
      },

      fetchUserData: async () => {
        const { tokens } = get();

        if (!tokens?.accessToken) {
          return null;
        }

        try {
          const baseUrl = getApiBaseUrl();
          const response = await fetch(`${baseUrl}/shared/users/login-with-token`, {
            method: "GET",
            credentials: 'include',
            headers: {
              "Content-Type": "application/json",
              "Authorization": `Bearer ${tokens.accessToken}`,
            },
          });

          if (!response.ok) {
            reportApiFailure('Failed to fetch user data', response.status);
            return null;
          }

          const data = await response.json();

          if (data.user && !data.error) {
            set({ userData: data.user });
            return data.user;
          }

          return null;
        } catch (error) {
          reportAppError('Error fetching user data', error);
          return null;
        }
      },

      logout: async () => {
        const { tokens, clearAuth } = get();

        try {
          const baseUrl = getApiBaseUrl();

          if (tokens) {
            await fetch(`${baseUrl}/shared/users/logout`, {
              method: "POST",
              credentials: 'include',
              headers: {
                "Content-Type": "application/json",
                "Authorization": `Bearer ${tokens.accessToken}`,
              },
              body: JSON.stringify({
                tokenId: tokens.tokenId,
              }),
            });
          }
        } catch (error) {
          reportAppError('Logout error', error, 'warning');
        } finally {
          clearAuth();
        }
      },
    }),
    {
      name: 'auth-storage',
      version: 2,
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        userData: state.userData,
        tokens: state.tokens,
      }),
      migrate: (persistedState, version) => {
        const state = persistedState as Record<string, unknown>;
        if (version < 2) {
          // Wipe stale userData from old user model shape
          // User will re-fetch fresh data on next login/token refresh
          return { ...state, userData: null };
        }
        return state;
      },
    }
  )
);

// ── SSE Connection Store ─────────────────────────────────────────────────────
// Single shared EventSource connection. All components read from this store
// instead of creating their own connections.

export interface RegionProgress {
  regionId: number;
  region: string;
  updatedRealms: number;
  totalRealms: number;
  percentComplete: number;
  isPartial: boolean;
  cycleId: string;
}

export type SSEListenerMap = {
  'auction-update': (timestamp: number, source?: string) => void;
  'token-update': (price: number, timestamp: number) => void;
  'data-ready': (isPartial: boolean, cycleId?: string) => void;
};

export type SSEEventType = keyof SSEListenerMap;

// Internal storage type uses Function to avoid generic variance issues
interface SSEListenerInternal {
  type: SSEEventType;
  // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
  callback: Function;
}

// Public type-safe listener for addListener callers
export type SSEListener<T extends SSEEventType = SSEEventType> = {
  type: T;
  callback: SSEListenerMap[T];
};

export interface SSEState {
  // Readable state
  isConnected: boolean;
  progress: RegionProgress | null;
  isUpdating: boolean;
  partialDataAvailable: boolean;

  // Internal (not for direct component use)
  _eventSource: EventSource | null;
  _reconnectTimeout: number | null;
  _reconnectAttempts: number;
  _currentRegion: string | null;
  _refCount: number;
  _listeners: SSEListenerInternal[];

  // Actions
  acquire: (region: string) => void;
  release: () => void;
  reconnect: () => void;
  addListener: <T extends SSEEventType>(listener: SSEListener<T>) => () => void;
  clearPartialDataAvailable: () => void;
}

const MAX_RECONNECT_ATTEMPTS = 10;
const BASE_RECONNECT_DELAY = 1000;

export const useSSE = create<SSEState>()(
  devtools(
    (set, get) => {
      const connect = () => {
        const state = get();
        const region = state._currentRegion;
        if (!region) return;

        // Clean up existing
        if (state._eventSource) {
          state._eventSource.close();
        }

        const url = `${getApiBaseUrl()}/events/stream/${region}`;

        const eventSource = new EventSource(url, { withCredentials: true });
        set({ _eventSource: eventSource });

        eventSource.onopen = () => {
          set({ isConnected: true, _reconnectAttempts: 0 });
        };

        eventSource.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            const { _listeners } = get();

            switch (data.type) {
              case 'connected':
                break;

              case 'auction-update':
                for (const l of _listeners) {
                  if (l.type === 'auction-update') l.callback(data.timestamp, data.source);
                }
                break;

              case 'token-update':
                window.dispatchEvent(new CustomEvent('sseTokenUpdate', {
                  detail: { region: data.region, price: data.price, timestamp: data.timestamp },
                }));
                for (const l of _listeners) {
                  if (l.type === 'token-update') l.callback(data.price, data.timestamp);
                }
                break;

              case 'region-progress':
                set({
                  progress: {
                    regionId: data.regionId,
                    region: data.region,
                    updatedRealms: data.updatedRealms,
                    totalRealms: data.totalRealms,
                    percentComplete: data.percentComplete,
                    isPartial: data.isPartial,
                    cycleId: data.cycleId,
                  },
                  isUpdating: data.isPartial,
                });
                break;

              case 'data-ready':
                set({ partialDataAvailable: data.isPartial === true });
                for (const l of _listeners) {
                  if (l.type === 'data-ready') l.callback(data.isPartial, data.cycleId);
                }
                break;
            }
          } catch (error) {
            reportAppError('[SSE] Failed to parse message', error);
          }
        };

        eventSource.onerror = () => {
          Sentry.logger.warn('[SSE] Connection error');
          eventSource.close();
          set({ isConnected: false, _eventSource: null });

          const { _reconnectAttempts, _refCount } = get();
          if (_reconnectAttempts < MAX_RECONNECT_ATTEMPTS && _refCount > 0) {
            const delay = BASE_RECONNECT_DELAY * Math.pow(2, _reconnectAttempts);

            const timeout = window.setTimeout(() => {
              set(s => ({ _reconnectAttempts: s._reconnectAttempts + 1 }));
              connect();
            }, delay);

            set({ _reconnectTimeout: timeout });
          }
        };
      };

      const disconnect = () => {
        const state = get();
        if (state._eventSource) {
          state._eventSource.close();
        }
        if (state._reconnectTimeout) {
          clearTimeout(state._reconnectTimeout);
        }
        set({
          _eventSource: null,
          _reconnectTimeout: null,
          isConnected: false,
          progress: null,
          isUpdating: false,
          partialDataAvailable: false,
        });
      };

      return {
        isConnected: false,
        progress: null,
        isUpdating: false,
        partialDataAvailable: false,

        _eventSource: null,
        _reconnectTimeout: null,
        _reconnectAttempts: 0,
        _currentRegion: null,
        _refCount: 0,
        _listeners: [],

        acquire: (region: string) => {
          const state = get();
          const newRefCount = state._refCount + 1;

          if (state._currentRegion !== region) {
            disconnect();
            set({ _currentRegion: region, _refCount: newRefCount });
            connect();
          } else if (!state._eventSource || state._eventSource.readyState === EventSource.CLOSED) {
            set({ _refCount: newRefCount });
            connect();
          } else {
            set({ _refCount: newRefCount });
          }
        },

        release: () => {
          const state = get();
          const newRefCount = Math.max(0, state._refCount - 1);
          set({ _refCount: newRefCount });

          if (newRefCount === 0) {
            disconnect();
          }
        },

        reconnect: () => {
          set({ _reconnectAttempts: 0 });
          connect();
        },

        addListener: <T extends SSEEventType>(listener: SSEListener<T>) => {
          const internal: SSEListenerInternal = { type: listener.type, callback: listener.callback };
          set(s => ({ _listeners: [...s._listeners, internal] }));
          return () => {
            set(s => ({ _listeners: s._listeners.filter(l => l !== internal) }));
          };
        },

        clearPartialDataAvailable: () => set({ partialDataAvailable: false }),
      };
    },
    { name: 'useSSE' }
  )
);

// ── Changelog Store ───────────────────────────────────────────────────────────

export interface ChangelogState {
  entries: ChangelogEntry[];
  unseenEntries: ChangelogEntry[];
  hasFetched: boolean;
  fetchChangelog: () => Promise<void>;
  markAllSeen: () => Promise<void>;
}

export const useChangelog = create<ChangelogState>()(
  devtools(
    (set, get) => ({
      entries: [],
      unseenEntries: [],
      hasFetched: false,

      fetchChangelog: async () => {
        try {
          const api = getApiService();
          const response = await api.get('/shared/changelog');
          if (!response.ok) return;
          const data: ChangelogResponse = await response.json();
          set({
            entries: data.entries,
            unseenEntries: data.entries.filter((e) => !e.seen),
            hasFetched: true,
          });
        } catch (error) {
          reportAppError('Failed to fetch changelog', error, 'warning');
        }
      },

      markAllSeen: async () => {
        const { unseenEntries } = get();
        if (unseenEntries.length === 0) return;
        const ids = unseenEntries.map((e) => e.entryId);
        try {
          const api = getApiService();
          await api.post('/shared/changelog/mark-seen', JSON.stringify({ entryIds: ids }));
          set((state) => ({
            entries: state.entries.map((e) => ({ ...e, seen: true })),
            unseenEntries: [],
          }));
        } catch (error) {
          reportAppError('Failed to mark changelog seen', error, 'warning');
        }
      },
    }),
    { name: 'useChangelog' }
  )
);